<template>
  <div id="multi-chart-box" class="h-100">
    <vue-element-loading :active="loading" :is-full-screen="false"/>
    <portal to="chart-select" class="">
      <div class="flex align-items-center">
      <div class="small-loader mb-3">
        <vue-element-loading :active="busy" :is-full-screen="false"/>
      </div>
      <b-form-group class="chart-select">
        <b-form-radio v-for="chart in chartTypes"
                      :button="true"
                      v-model="activeChart"
                      size="sm"
                      name="chart-select"
                      :value="chart.value"
        >
          <custom-icon :icon="chart.icon"
                       class="cursor-pointer"></custom-icon>
        </b-form-radio>
      </b-form-group>
      </div>
    </portal>
    <!--<b-btn @click="updateAxis">Click me</b-btn>-->
    <div ref="bar-chart-panel" v-show="activeChart === 'bar'">
      <radio-stat-panel v-if="barChartDataSelectOptionsLocal"
                        :options="barChartDataSelectOptionsLocal"
                        @change="updateBarChartDataset"
      />
      <checkbox-stat-panel v-if="barChartFilterOptionsLocal"
                           :options="barChartFilterOptionsLocal"
                           label-color="#2C3E38"
                           @change="updateBarChart"
      />
      <apexchart type="bar"
                 height="350"
                 :options="barChartOptions"
                 :series="barChartData"
                 ref="bar-chart"
                 @legendClick="onLegendClick"
      ></apexchart>
    </div>
    <div ref="line-chart-panel" v-show="activeChart === 'line'">
      <radio-stat-panel v-if="lineChartDataSelectOptionsLocal"
                        :options="lineChartDataSelectOptionsLocal"
                        label-color="#2C3E38"
                        @change="updateLineChartDataset"
      />
      <checkbox-stat-panel v-if="lineChartFilterOptionsLocal"
                           :options="lineChartFilterOptionsLocal"
                           @change="updateLineChart"
      />
      <apexchart type="line"
                 height="350"
                 :options="lineChartOptions"
                 :series="lineChartData"
                 ref="line-chart"
                 @legendClick="onLegendClick"
      >
      </apexchart>
    </div>
    <div ref="table-panel" v-show="activeChart === 'table'" class="table-container">
      <ag-grid-vue
          :cacheQuickFilter="true"
          :columnDefs="chartOptions.table.columnDefs"
          :floatingFilter="false"
          :gridOptions="chartOptions.table.gridOptions"
          @gridReady="onGridReady"
          :headerHeight="chartOptions.table.rowHeight"
          :rowData="chartOptions.table.rowData"
          :rowHeight="chartOptions.table.rowHeight"
          :suppressContextMenu="true"
          :suppressExcelExport="true"
          domLayout="autoHeight"
          class="ag-theme-balham"
          @modelUpdated="onTableDataUpdated"
      ></ag-grid-vue>
    </div>
    <portal to="filters">
      <date-range-filters :interval="interval"
                          :display-data-range="displayDataRange"
                          :start-date="dataStartDate"
                          :end-date="dynamicEndDate"
                          :range-start-date="displayStartDate"
                          :range-end-date="displayEndDate"
                          :data-size="dataSize"
                          @update:interval="updateInterval"
                          @update:displayDataRange="updateDisplayDateRange"
                          @legendClick="onLegendClick"
      />
    </portal>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import GridMixin from '@/components/Grid/GridMixin';
import { AgGridVue } from 'ag-grid-vue';
import RadioStatPanel from '@/components/Dashboard/RadioStatPanel';
import CheckboxStatPanel from '@/components/Dashboard/CheckboxStatPanel';
import CustomIcon from '@/components/Custom/CustomIcon';
import DateRangeFilters from '@/components/Dashboard/DateRangeFilters';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
dayjs.extend(quarterOfYear);
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrAfter);
import { cloneDeep, isEmpty } from 'lodash';

export default {
  name: "MultiChartBox",
  components: {
    CustomIcon,
    apexchart: VueApexCharts,
    RadioStatPanel,
    CheckboxStatPanel,
    DateRangeFilters,
    AgGridVue,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    chartOptions: {
      type: Object,
      required: true,
    },
    dataLabels: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    interval: {
      type: String,
      required: true,
    },
    barChartDataSelectOptions: {
      type: Array,
    },
    barChartFilterOptions: {
      type: Array,
    },
    lineChartFilterOptions: {
      type: Array,
    },
    lineChartDataSelectOptions: {
      type: Array,
    },
    syncChartLegends: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeChart: 'bar',
      barChartDataKey: 0,
      dataStartDate: null,
      dataEndDate: null,
      displayStartDate: null,
      displayEndDate: null,
      displayDataRange: 11,
      datasets: {
        bar: [],
        line: [],
        table: [],
      },
      chartLabels: [],
      filteredData: [],
      chartLoading: false,
      lineChartDataKey: 0,

      bcData: [{
        data: [],
      }],

      defaultChartOptions: {
        barChart: {
          chart: {
            animations: {
              enabled: false,
            },
            type: 'bar',
            height: 350,
            stacked: true,
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: true,
            },
          },
          states: {
            hover: {
              filter: {
                type: 'none',
                value: 0,
              },
            },
          },
          responsive: [{
            breakpoint: 480,
            options: {
              legend: {
                position: 'bottom',
                offsetX: -10,
                offsetY: 0,
              },
            },
          }],
          colors: [],
          plotOptions: {
            bar: {
              borderRadius: 0,
              horizontal: false,
              columnWidth: '25%',
              colors: {
                backgroundBarColors: ['#FFFFFF'],
                backgroundBarOpacity: 0,
              },
            },
          },
          xaxis: {},
          legend: {
            position: 'top',
            horizontalAlign: 'right',
            offsetY: 0,
            markers: {
              width: 16,
              height: 16,
              customHTML: function() {return '<img src="/tick.svg"/>'},
            },
            onItemHover: {
              highlightDataSeries: false,
            },
          },
          dataLabels: {
            enabled: false,
          },
          fill: {
            opacity: 1,
          },
          tooltip: {

          },
        },
        lineChart: {
          chart: {
            animations: {
              enabled: false,
            },
            type: 'line',
            height: 350,
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: true,
            },
          },
          responsive: [{
            breakpoint: 480,
            options: {
              legend: {
                position: 'bottom',
                offsetX: -10,
                offsetY: 0,
              },
            },
          }],
          legend: {
            position: 'top',
            horizontalAlign: 'right',
            offsetY: 0,
            markers: {
              width: 16,
              height: 16,
              radius: 2,
              customHTML: function() {return '<img src="/tick.svg"/>'},
            },
          },
          stroke: {
            width: 3,
          }
        }
      },
      chartTypes: [
        {
          icon: 'barchart',
          value: 'bar',
        },
        {
          icon: 'linechart',
          value: 'line',
        },
        {
          icon: 'table',
          value: 'table',
        },
      ],
      barChartDataSelectOptionsLocal: [],
      barChartFilterOptionsLocal: [],
      lineChartFilterOptionsLocal: [],
      lineChartDataSelectOptionsLocal: [],
      legendsToggled: [],
      internalLoading: false,
    }
  },
  computed: {
    busy() {
        return this.internalLoading || this.loading;
    },
    xAxisCategories() {
        return this.data.map(({ chartLabel }) => chartLabel);
    },
    barChartOptions() {
      const options = {
        ...this.defaultChartOptions.barChart,
        ...this.chartOptions.barChart,
      };
      options.xaxis.categories = this.xAxisCategories;
      return options;
    },
    barChartData() {
      if (!this.datasets.bar) {
        return [];
      }

      return this.datasets.bar[this.barChartDataKey];
    },
    lineChartOptions() {
      const options = {
        ...this.defaultChartOptions.lineChart,
        ...this.chartOptions.lineChart,
      };

      options.xaxis.categories = this.xAxisCategories;
      return options;
    },
    lineChartData() {
      if (!this.datasets.line) {
        return [];
      }

      return this.datasets.line[this.lineChartDataKey];
    },
    dataSize() {
      if (!this.data || this.data.length === 0) {
        return 0;
      }
      return (this.data.length > 36) ? this.data.length : 36;
    },
    dynamicEndDate() {
      const threeYears = this.dataStartDate.add(35, 'month');
      return (this.dataEndDate.isAfter(threeYears)) ? this.dataEndDate :  threeYears;
    },
  },

  watch: {
    data: {
      handler(newValue) {
        this.formatData();
      },
      immediate: false,
    },
    activeChart: {
      handler(newValue) {
        if (newValue === 'table') {
          this.chartOptions.table.gridApi.sizeColumnsToFit();
        } else {
          const data = {
            startDate: this.displayStartDate,
            endDate: this.displayEndDate,
          }
          this.updateDisplayDateRange(data);
        }
      },
    },
    barChartDataKey(newValue) {
      this.updateChartOptions('bar-chart', this.datasets.bar[newValue]);
    },
    lineChartDataKey(newValue) {
      this.updateChartOptions('line-chart', this.datasets.line[newValue]);
    },
  },
  created() {
    this.barChartDataSelectOptionsLocal = this.barChartDataSelectOptions;
    this.barChartFilterOptionsLocal = this.barChartFilterOptions;
    this.lineChartDataSelectOptionsLocal = this.lineChartDataSelectOptions;
    this.lineChartFilterOptionsLocal = this.lineChartFilterOptions;
    this.formatData();
  },
  mounted() {

  },
  methods: {
    formatData() {
      this.setDateData();
      this.filterData();

      // Labels for axis
      //this.formatChartLabels();

      // Bar chart
      this.formatChartData();

      // Table
      this.formatTableHeaders();
      this.formatTableData();
    },
    setDateData() {
      this.dataStartDate = (this.data.length === 0) ? dayjs() : dayjs(this.data[0].startDate);
      this.dataEndDate = (this.data.length === 0) ? dayjs() : dayjs(this.data[this.data.length - 1].startDate);
      this.displayStartDate = (this.data.length === 0) ? dayjs() : dayjs(this.data[0].startDate);
      this.displayEndDate = this.displayStartDate.add(this.displayDataRange, 'month');
    },
    filterData() {
      this.filteredData = {};

      if (!this.data || this.data.length === 0) {
        return;
      }
      this.filteredData = this.data.filter((obj)=>{
        return dayjs(obj.startDate).isSameOrAfter(this.displayStartDate)
            && dayjs(obj.startDate).isSameOrBefore(this.displayEndDate);
      });
    },
    formatChartData() {
      this.internalLoading = true;
      const chartData = [];
      const template = [];
      const filterCounts = [];

      this.dataLabels.forEach((item, key) => {
        template.push({
          name: item.name,
          data: [],
        });
        filterCounts.push(0);
      });

      const numDatasets = (this.data.length === 0) ? 1 : this.data[0].data.length;
      const counts = [];
      for (let i = 0; i < numDatasets; i++) {
        chartData.push(cloneDeep(template));
        counts.push(0);
      }
      let intervalDate = cloneDeep(this.displayStartDate);
      while(intervalDate.isSameOrBefore(this.displayEndDate)) {
        if (isEmpty(this.filteredData) || intervalDate.isBefore(this.dataStartDate) || intervalDate.isAfter(this.dataEndDate)) {
          for (let i = 0; i < numDatasets; i++) {
            for (let n = 0; n < chartData[i].length; n++) {
              let label = (this.interval === 'quarter')
                  ? 'Q' + intervalDate.quarter() + ' ' + intervalDate.format('YY')
                  : intervalDate.format('MMM YY');
              chartData[i][n].data.push({
                x: label,
                y: 0,
              });
            }
          }
          intervalDate = intervalDate.add(1, this.interval);
        } else {
          this.filteredData.forEach(row => {
            row.data.forEach((dataset, dataKey) => {
              dataset.forEach((item, key) => {
                chartData[dataKey][key].data.push({
                  x: row.chartLabel,
                  y: item.count,
                });
                counts[dataKey] += item.count;
                filterCounts[key] += item.count;
              });
            });
            intervalDate = intervalDate.add(1, this.interval);
          });
        }
      }

      this.setBarChartData(chartData, counts, filterCounts);
      this.setLineChartData(chartData, counts, filterCounts);
      this.internalLoading = false;

      //this.$refs['bar-chart'].zoomX(6,10);
    },
    setBarChartData(chartData, counts, filterCounts) {
      if (this.barChartDataSelectOptionsLocal) {
        for (let i = 0; i < counts.length; i++) {
          this.barChartDataSelectOptionsLocal[i].value = counts[i];
        }
      }

      if (this.barChartFilterOptionsLocal) {
        for (let i = 0; i < filterCounts.length; i++) {
          this.barChartFilterOptionsLocal[i].value = filterCounts[i];
        }
      }

      this.datasets.bar = chartData;
      // Get current series
      const series = this.datasets.bar[this.barChartDataKey];
      this.updateChartOptions('bar-chart', series);
    },
    setLineChartData(chartData, counts, filterCounts) {
      if (this.lineChartDataSelectOptionsLocal) {
        for (let i = 0; i < counts.length; i++) {
          this.lineChartDataSelectOptionsLocal[i].value = counts[i];
        }
      }

      if (this.lineChartFilterOptionsLocal) {
        for (let i = 0; i < filterCounts.length; i++) {
          this.lineChartFilterOptionsLocal[i].value = filterCounts[i];
        }
      }

      this.datasets.line = chartData;

      // Get current series
      const series = this.datasets.line[this.lineChartDataKey];

      this.updateChartOptions('line-chart', series);
    },
    /**
     * Update chart options for given ref, requires series
     * Will automatically set the x-axis
     * */
    updateChartOptions(ref, series) {
      const isBarChart = ref === 'bar-chart';
      // Get unique x axis labels as categories for options
      const categories = new Set(series.flatMap(({ data }) => data.map(({ x }) => x)));
      // We just get items in an array rather than having a x value label
      // This fixes a bug that causes the first legend item to toggle and removes the BAR chart
      const data = series.map(({ data: d, name }) => ({ data: d.map(({ y }) => y), name }));

      // Get just the names, used for legend
      const names = series.map(({ name }) => name);

      // Incase it hasn't rendered yet
      this.$nextTick(() => {
        this.$refs[ref].updateOptions({
          series: data,
          xaxis: {
            type: 'category',
            categories: [...categories],
          },
        });

        // Hide series that were previously disabled
        try {
          this.legendsToggled.forEach(index => this.$refs[ref].hideSeries(names[index]));

          const hideCustomSeries = (reference, opts) => {
            opts.filter(({ checked }) => checked === false)
                .forEach(({ label }) => reference.hideSeries(label));
          };

          if (isBarChart && this.barChartFilterOptionsLocal) {
            hideCustomSeries(this.$refs[ref], this.barChartFilterOptionsLocal);
          }

          if (!isBarChart && this.lineChartFilterOptionsLocal) {
            hideCustomSeries(this.$refs[ref], this.lineChartFilterOptionsLocal);
          }
        } catch (e) {
          // There is an error that occurs that isn't an issue
          // It is to do with getAttribute
        }
      });
    },
    formatTableHeaders() {
      const colDefs = [{
        headerName: this.uppercaseFirst(this.interval),
        field: 'period',
      }];

      this.dataLabels.forEach((item, key) => {
        colDefs.push({
          headerName: item.name,
          field: 'data_' + item.id,
        });
      });

      this.chartOptions.table.columnDefs = colDefs;
    },
    formatTableData() {
      const tableData = [];

      let intervalDate = cloneDeep(this.displayStartDate);
      while(intervalDate.isSameOrBefore(this.displayEndDate)) {
        if (isEmpty(this.filteredData) || intervalDate.isBefore(this.dataStartDate) || intervalDate.isAfter(this.dataEndDate)) {
          let date = (this.interval === 'quarter')
              ? 'Q' + intervalDate.quarter() + ' ' + intervalDate.format('YYYY')
              : intervalDate.format('MMMM YYYY');
          const rowData = {
            period: date,
          };

          this.dataLabels.forEach((item, key) => {
            let field = 'data_' + item.id;
            rowData[field] = 0;
          });
          tableData.push(rowData);

          intervalDate = intervalDate.add(1, this.interval);
        } else {
          this.filteredData.forEach(row => {
            let rowData = {
              period: row.tableLabel,
            };
            row.data[0].forEach((item, key) => {
              let field = 'data_' + item.id;
              rowData[field] = item.count;
            });

            tableData.push(rowData);
            intervalDate = intervalDate.add(1, this.interval);
          });
        }

        this.chartOptions.table.rowData = tableData;
      }
    },
    updateBarChartDataset(key) {
      this.barChartDataKey = key;
    },
    updateLineChartDataset(key) {
      this.lineChartDataKey = key;
    },
    updateBarChart(value) {
      const checked = (value.checked) ? true : false;
      const key = this.barChartFilterOptionsLocal.findIndex(item => item.formValue === value.checkboxValue);
      this.barChartFilterOptionsLocal[key].checked = checked;
      const item = this.barChartFilterOptionsLocal.find(item => item.formValue === value.checkboxValue);
      this.$refs['bar-chart'].toggleSeries(item.label);

      if (this.syncChartLegends) {
        this.lineChartFilterOptionsLocal[key].checked = checked;
      }
    },
    updateLineChart(value) {
      const checked = (value.checked) ? true : false;
      const key = this.lineChartFilterOptionsLocal.findIndex(item => item.formValue === value.checkboxValue);
      this.lineChartFilterOptionsLocal[key].checked = checked;
      const item = this.lineChartFilterOptionsLocal.find(item => item.formValue === value.checkboxValue);
      this.$refs['line-chart'].toggleSeries(item.label);

      if (this.syncChartLegends) {
        this.barChartFilterOptionsLocal[key].checked = checked;
      }
    },
    updateDisplayDateRange(data) {
      if (data.duration) {
        if (data.reset) {
          this.displayStartDate = this.dataStartDate;
        }
        this.displayEndDate = this.displayStartDate.add(data.duration, 'month');
        this.displayDataRange = data.value;
      } else {
        this.displayStartDate = dayjs(data.startDate);
        this.displayEndDate = dayjs(data.endDate);
      }

      // Don't allow the axis to go beyond the end of the data range
      if (this.displayEndDate.isAfter(this.dynamicEndDate)) {
        this.displayEndDate = cloneDeep(this.dynamicEndDate);
        this.displayStartDate = this.displayEndDate.subtract(this.displayDataRange, 'month');
      }

      this.filterData();
      this.formatChartData();
      this.formatTableData();
    },
    updateInterval(interval) {
      this.$emit('reload', interval);
    },
    uppercaseFirst(string) {
      if (!string) {
        return '';
      }

      return string.charAt(0)
          .toUpperCase() + string.slice(1);
    },
    async onGridReady(params) {
      this.chartOptions.table.gridApi = params.api;
      this.chartOptions.table.columnApi = params.columnApi;
    },
    onTableDataUpdated() {
      if (this.chartOptions.table.gridApi) {
        this.chartOptions.table.gridApi.sizeColumnsToFit();
      }
    },
    onLegendClick(a, index) {
      if (this.legendsToggled.includes(index)) {
        this.legendsToggled.splice(this.legendsToggled.indexOf(index), 1);
        return;
      }

      this.legendsToggled.push(index);
    },
  },
}
</script>

<style lang="scss">
  .apexcharts-legend-series {
    margin-right: 25px !important;
    .apexcharts-legend-marker {
      margin-right: 8px;
      top: 4px !important;
      img {
        width: 13px;
        height: 13px;
        filter: brightness(0) invert(1);
        position: absolute;
        top: 1px;
        left: 2px;
      }
      &.apexcharts-inactive-legend {
        img {
          display: none;
        }
      }
    }
  }
  .chart-select {
    .btn {
      background: #FFFFFF;
      border: 0;
      img {
        filter: opacity(50%);
      }
      &.active {
        background: #FFFFFF !important;
        img {
          filter: opacity(100%);
        }
      }

    }
  }
  .table-container {
    max-height: 500px;
    overflow-y: auto;
  }

  .small-loader {
    width: 15px;
    height: 15px;
    svg {
      width: 15px;
      height: 15px;
    }
  }

</style>
